/**
 * What an asset register actually knows.
 *
 * The book's line for this module: "The useful question is never how many
 * laptops exist but what the fleet is worth, what is idle, and which items are
 * out with someone who has already left."
 *
 * Two of those three are answerable from the data. The third is not: an Asset
 * carries no purchase cost, no depreciation basis and no book value, so every
 * money figure the book draws — the $412,900 headline, the Value column, the
 * "$6,240 held by leavers" sub-line — has nothing behind it. Nothing in this
 * module returns a currency amount, and the screen says so rather than drawing
 * an invented one. `MISSING_FACTS` below is the list, kept here so the screen
 * and this file cannot drift about which gaps exist.
 *
 * Every date comparison is done on a UTC calendar day. Reading local fields off
 * a Date puts an asset bought at 23:30 UTC on the 31st into the following month
 * for anyone east of UTC — the calendar-day bug this codebase has hit three
 * times.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * The book's panels that no field supports, and the field each one would need.
 *
 * Stated rather than drawn, the way the performance dashboard names the two
 * cycle stages it cannot measure. A number invented from nothing reads as a
 * measurement, which is worse than an absent one.
 */
export const MISSING_FACTS = [
  { key: "bookValue", label: "Book value", needs: "a purchase cost and depreciation basis on Asset" },
  { key: "refreshAge", label: "Refresh age", needs: "a refresh-age policy per asset type" },
  { key: "repair", label: "In repair", needs: "a repair state on Asset (status is only active/inactive)" },
  { key: "dueBack", label: "Due back", needs: "a return-due date on Asset" },
  { key: "assignedSince", label: "Assigned since", needs: "an assignment date on Asset" },
];

// ---------------------------------------------------------------- calendar

/** The UTC calendar day of a value, as {y, m, d}. Null if it is not a date. */
const utcParts = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { y: value.getUTCFullYear(), m: value.getUTCMonth() + 1, d: value.getUTCDate() };
  }
  const s = String(value);
  // An ISO string already starts with its UTC calendar date, so slicing beats
  // building a Date and reading local fields back out of it.
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return { y: Number(iso[1]), m: Number(iso[2]), d: Number(iso[3]) };
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return { y: parsed.getUTCFullYear(), m: parsed.getUTCMonth() + 1, d: parsed.getUTCDate() };
};

/** Whole days since the epoch, on the UTC calendar. Null if not a date. */
export const dayNumber = (value) => {
  const p = utcParts(value);
  if (!p) return null;
  return Math.floor(Date.UTC(p.y, p.m - 1, p.d) / 86400000);
};

/** Whole days from `from` to `to`, both normalised to a UTC calendar day. */
export const daysBetween = (from, to) => {
  const a = dayNumber(from);
  const b = dayNumber(to);
  if (a == null || b == null) return null;
  return b - a;
};

/**
 * Completed years between two dates, on the calendar rather than on 365.25.
 *
 * An asset bought on 29 Feb is four years old on 28 Feb, not four years and a
 * fraction of a day old, and a fleet-age bucket that disagrees with the
 * purchase date on the record is the kind of thing a finance reviewer notices.
 */
export const yearsSince = (value, today) => {
  const p = utcParts(value);
  const t = utcParts(today);
  if (!p || !t) return null;
  let years = t.y - p.y;
  if (t.m < p.m || (t.m === p.m && t.d < p.d)) years -= 1;
  return years;
};

/** "14 Mar" — spelled from a table, because ICU renders "Sept" for September. */
export const shortDate = (value) => {
  const p = utcParts(value);
  if (!p || p.m < 1 || p.m > 12) return null;
  return `${p.d} ${MONTHS[p.m - 1]}`;
};

/** "Jan 2023" — the register's date column, same month table, same reason. */
export const monthYear = (value) => {
  const p = utcParts(value);
  if (!p || p.m < 1 || p.m > 12) return null;
  return `${MONTHS[p.m - 1]} ${p.y}`;
};

export const share = (part, whole) => (whole > 0 ? part / whole : 0);

// ------------------------------------------------------------------ people

/**
 * Has the holder of this asset left?
 *
 * The book: "An unreturned asset belonging to a leaver is the one thing this
 * module escalates." There is no `returned` flag anywhere, so "not returned"
 * is derived — an asset still pointing at someone whose employment has ended
 * is, by definition, still out with them.
 *
 * Two fields say it, and either is enough: `employmentStatus: "exited"` is the
 * lifecycle state, and `isActive: false` is the access flag that offboarding
 * clears. A record that has one but not the other is still a leaver.
 */
export const isLeaver = (employee) => {
  if (!employee) return false;
  return employee.employmentStatus === "exited" || employee.isActive === false;
};

export const holderName = (employee) => {
  const name = `${employee?.fname || ""} ${employee?.lname || ""}`.trim();
  return name || "";
};

/**
 * An asset's holder, resolved against the employee directory.
 *
 * The asset endpoint populates only `fname lname aliasId` from Employee, so the
 * leaver check cannot be made from the asset alone. The directory an admin
 * already has in the org cache carries `employmentStatus`, `isActive` and
 * `lastWorkingDay`, so the two are joined here rather than in the screen.
 */
export const resolveHolder = (asset, employeesById) => {
  const ref = asset?.employee;
  if (!ref) {
    // A pool asset: held by a department, not a person. The book draws this as
    // "Operations pool", and it is in use — nobody can pick it up off a shelf.
    const dept = asset?.department?.name;
    return dept ? { kind: "department", name: `${dept} pool`, leaver: false } : null;
  }
  const id = String(ref?._id || ref);
  const full = (employeesById instanceof Map ? employeesById.get(id) : employeesById?.[id]) || null;
  const name = holderName(full) || holderName(ref) || "Unknown";
  const leaver = isLeaver(full);
  return {
    kind: "person",
    id,
    name,
    leaver,
    // Only meaningful for a leaver; the screen prints it beside the name.
    lastWorkingDay: full?.lastWorkingDay || null,
    profileImage: full?.profileImage,
  };
};

// ------------------------------------------------------------------ states

/**
 * The states an asset can be in.
 *
 * The book names five — In use, In stock, In repair, Retiring, Not returned —
 * and two of them have no field behind them. `status` is an enum of exactly
 * `active` and `inactive`; there is no repair state and no refresh-age policy,
 * so "In repair" and "Retiring" are not derivable and are not invented here.
 *
 * "Retired" takes the place the book gives "Retiring": `status: "inactive"` is
 * a real state a real asset is in, and every asset has to land in exactly one
 * bucket for the state bar's widths to sum to the fleet.
 *
 * Colours are tokens, not the book's raw hexes — the same status palette the
 * rest of the product uses, so "not returned" is the danger colour here and in
 * every other module.
 */
export const STATES = [
  { key: "inUse", label: "In use", tone: "success" },
  { key: "inStock", label: "In stock", tone: "muted" },
  { key: "retired", label: "Retired", tone: "info" },
  { key: "notReturned", label: "Not returned", tone: "danger" },
];

export const STATE_LABEL = STATES.reduce((m, s) => ({ ...m, [s.key]: s.label }), {});
export const STATE_TONE = STATES.reduce((m, s) => ({ ...m, [s.key]: s.tone }), {});

/**
 * Exactly one state per asset, and the order is the priority.
 *
 * "Not returned" is tested first on purpose: an asset out with someone who has
 * left is the one thing this module escalates, and it stays that even if the
 * record was also marked inactive on the way out of the door.
 */
export const assetState = (asset, holder) => {
  if (holder?.kind === "person" && holder.leaver) return "notReturned";
  if (asset?.status === "inactive") return "retired";
  if (holder) return "inUse";
  return "inStock";
};

/**
 * The one stacked bar in PayLoom.
 *
 * The book earns it here and nowhere else: every asset is in exactly one state,
 * so the widths sum to the fleet and the bar is a whole rather than a
 * comparison. Empty states keep their row in the legend — a state with nothing
 * in it is information, and dropping it makes four states look like three.
 */
export const stateCounts = (rows = []) => {
  const counts = Object.fromEntries(STATES.map((s) => [s.key, 0]));
  rows.forEach((r) => {
    if (counts[r.state] === undefined) return;
    counts[r.state] += 1;
  });
  const total = rows.length;
  return {
    total,
    rows: STATES.map((s) => ({
      ...s,
      count: counts[s.key],
      share: share(counts[s.key], total),
    })),
  };
};

// -------------------------------------------------------------- the fleet

/**
 * What the fleet is made of.
 *
 * The book's panel is "Where the value sits", a Category / Qty / Value / Share
 * table. There is no value, so this returns Qty and Share and the screen
 * renames the panel to what it is showing. `category` on the model is only
 * `machine | software`, which is two rows and tells nobody anything; `type` is
 * the field that actually holds "Laptop", "Mobile", "Desktop", so the grouping
 * is by type.
 *
 * Share is share of the *count*, and the screen says so — a quantity share read
 * as a value share is exactly the misreading the missing money column invites.
 */
export const typeBreakdown = (rows = []) => {
  const byType = new Map();
  rows.forEach((r) => {
    const name = (r.type || "").trim() || "Uncategorised";
    byType.set(name, (byType.get(name) || 0) + 1);
  });
  const total = rows.length;
  const out = [...byType.entries()]
    .map(([name, count]) => ({ name, count, share: share(count, total) }))
    // Biggest first, ties broken by name so the order is stable between loads.
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return {
    total,
    rows: out.map((r, i) => ({ ...r, colorIndex: i })),
  };
};

export const AGE_BUCKETS = [
  { key: "u1", label: "Under 1", min: 0, max: 0 },
  { key: "1to2", label: "1–2", min: 1, max: 1 },
  { key: "2to3", label: "2–3", min: 2, max: 2 },
  { key: "3to4", label: "3–4", min: 3, max: 3 },
  { key: "o4", label: "Over 4", min: 4, max: Infinity },
];

/**
 * Age of the fleet, in completed years since purchase.
 *
 * The only purchase date on the model is `purchasedOn`, and the create/update
 * validators accept it for software but not for machines — so most hardware
 * has none. Those assets are counted as `undated` and reported under the chart
 * rather than being dropped silently: a five-column chart drawn from nine
 * records out of three hundred is a lie told with real numbers.
 *
 * A purchase date in the future lands in "Under 1". It is bad data rather than
 * a negative age, and inventing a sixth bucket for it would put a column on the
 * chart that means "someone typo'd a year".
 */
export const fleetAge = (rows = [], today = new Date()) => {
  const counts = Object.fromEntries(AGE_BUCKETS.map((b) => [b.key, 0]));
  let dated = 0;
  let undated = 0;
  rows.forEach((r) => {
    const years = yearsSince(r.purchasedOn, today);
    if (years == null) {
      undated += 1;
      return;
    }
    dated += 1;
    const bucket = AGE_BUCKETS.find((b) => years >= b.min && years <= b.max) || AGE_BUCKETS[0];
    counts[bucket.key] += 1;
  });
  const buckets = AGE_BUCKETS.map((b) => ({ key: b.key, label: b.label, count: counts[b.key] }));
  return {
    buckets,
    dated,
    undated,
    total: rows.length,
    max: Math.max(0, ...buckets.map((b) => b.count)),
  };
};

/**
 * Licences running out.
 *
 * This is not the book's "Retiring", which means hardware past its refresh age
 * and has no field behind it. `expiredOn` is a real date on a real record, and
 * a licence lapsing inside ninety days is a real thing to chase — so the sixth
 * counter states what it measures rather than borrowing the book's label.
 */
export const expiringSoon = (rows = [], today = new Date(), within = 90) => {
  let soon = 0;
  let lapsed = 0;
  rows.forEach((r) => {
    const days = daysBetween(today, r.expiredOn);
    if (days == null) return;
    if (days < 0) lapsed += 1;
    else if (days <= within) soon += 1;
  });
  return { soon, lapsed, within };
};

// ---------------------------------------------------------- register rows

/**
 * One row of the register, with its state already decided.
 *
 * The book: "A badge in the register is always one of those five states; why it
 * is in that state — service due, screen cracked — is a line under the asset,
 * never a sixth badge." `reason` is that line. It is only ever filled from a
 * fact on the record: a lapsed or lapsing licence date, or an asset marked
 * inactive. There is no repair or damage field to read, so nothing invents one.
 */
export const registerRow = (asset, employeesById, today = new Date()) => {
  const holder = resolveHolder(asset, employeesById);
  const state = assetState(asset, holder);
  const detail = [asset?.manufacturer, asset?.model || asset?.version]
    .map((v) => (v || "").trim())
    .filter(Boolean)
    .join(" · ");

  const expiryDays = daysBetween(today, asset?.expiredOn);
  let reason = null;
  if (expiryDays != null && expiryDays < 0) reason = `Licence lapsed ${shortDate(asset.expiredOn)}`;
  else if (expiryDays != null && expiryDays <= 90) reason = `Licence expires ${shortDate(asset.expiredOn)}`;
  else if (state === "retired") reason = "Marked inactive";

  return {
    _id: asset?._id,
    // No asset-tag field exists. The serial number is the identifier the
    // register actually carries, and it is the thing people read character by
    // character off the back of a machine — so it gets the monospace column.
    tag: (asset?.serialNumber || "").trim(),
    name: asset?.name || "—",
    detail,
    reason,
    type: asset?.type || "",
    category: asset?.category || "",
    status: asset?.status || "active",
    purchasedOn: asset?.purchasedOn || null,
    expiredOn: asset?.expiredOn || null,
    // Not an assignment date: there is none. This is when the asset entered
    // service, and the column is labelled that way.
    inServiceOn: asset?.activeServiceDate || asset?.purchasedOn || null,
    holder,
    state,
  };
};

export const buildRegister = (assets = [], employees = [], today = new Date()) => {
  const byId = new Map((employees || []).map((e) => [String(e?._id), e]));
  return (assets || []).map((a) => registerRow(a, byId, today));
};

/** Free-text match over the three things the book's placeholder names. */
export const matchesQuery = (row, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return [row?.tag, row?.name, row?.detail, row?.type, row?.holder?.name]
    .some((v) => String(v || "").toLowerCase().includes(q));
};

/**
 * The six counters.
 *
 * Three of the book's six carry a colour, "the three that cost money". Two of
 * those three — items in repair, hardware past its refresh age — have no field
 * behind them, so this returns the ones that are real: assets out with a
 * leaver, and licences about to lapse.
 */
export const summarise = (rows = [], today = new Date()) => {
  const states = stateCounts(rows);
  const byKey = Object.fromEntries(states.rows.map((r) => [r.key, r.count]));
  const expiring = expiringSoon(rows, today);
  const leavers = rows.filter((r) => r.state === "notReturned");
  return {
    total: rows.length,
    inUse: byKey.inUse,
    inUseShare: share(byKey.inUse, rows.length),
    inStock: byKey.inStock,
    retired: byKey.retired,
    notReturned: byKey.notReturned,
    // Distinct people, not assets: "held by 2 leavers" is what an offboarding
    // owner chases, and one person can be holding three things.
    leaverCount: new Set(leavers.map((r) => r.holder?.id).filter(Boolean)).size,
    expiring,
  };
};
